import React from 'react';
import { motion } from 'framer-motion';
import { Check } from 'lucide-react';
import { useRegion } from '../contexts/RegionContext';

const PricingPlans = ({ onSelectPlan }) => {
  const { getPricing, region, t } = useRegion();
  const pricing = getPricing();

  const plans = [
    {
      id: 'starter',
      name: "Starter",
      price: pricing.starter.price,
      period: pricing.starter.period,
      features: ["Up to 1,000 devices", "URL Scanner", "SMS Analysis", "Basic IoT Security", "Email Support", "Standard SLA"],
      highlighted: false
    },
    {
      id: 'enterprise',
      name: "Enterprise",
      price: pricing.enterprise.price,
      period: pricing.enterprise.period,
      features: ["Unlimited devices", "All Security Modules", "AI Security Agents", "Custom Integrations", "24/7 SOC Support", "99.99% SLA"],
      highlighted: true
    },
    {
      id: 'carrier',
      name: "Carrier",
      price: pricing.carrier.price,
      period: pricing.carrier.period,
      features: ["Multi-tenant deployment", "Full CSP Integration", "White-label options", "Dedicated infrastructure", "On-premise available", "Custom SLA"],
      highlighted: false
    }
  ];

  return (
    <div className="py-8">
      <div className="text-center mb-8">
        <span className="text-xs uppercase tracking-[0.2em] font-semibold text-orange-500">
          Flexible Pricing
        </span>
        <h2 className="text-3xl font-bold text-white mt-2">
          Scale with Confidence
        </h2>
        <p className="text-neutral-400 mt-2">
          Prices shown in {region.currency} ({region.name})
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        {plans.map((plan) => (
          <motion.div
            key={plan.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            data-testid={`pricing-card-${plan.id}`}
            className={`relative rounded-2xl p-6 border ${
              plan.highlighted 
                ? 'bg-gradient-to-b from-orange-500/10 to-transparent border-orange-500/30' 
                : 'bg-[#1a1a1a] border-white/10'
            }`}
          >
            {plan.highlighted && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <span className="px-4 py-1 bg-gradient-to-r from-[#F7B731] to-[#F26419] text-black text-xs font-semibold rounded-full">
                  Most Popular
                </span>
              </div>
            )}
            <h3 className="text-xl font-bold text-white mb-2">{plan.name}</h3>
            <div className="mb-4">
              <span className="text-3xl font-black text-white">{plan.price}</span>
              <span className="text-neutral-500">{plan.period}</span>
            </div>
            <ul className="space-y-2 mb-6">
              {plan.features.map((feature, i) => (
                <li key={`${plan.id}-feature-${i}`} className="flex items-center gap-2 text-sm text-neutral-400">
                  <Check className="w-4 h-4 text-orange-500 flex-shrink-0" />
                  {feature}
                </li>
              ))}
            </ul>
            <button 
              data-testid={`pricing-select-${plan.id}`}
              onClick={() => onSelectPlan && onSelectPlan(plan)}
              className={`w-full py-3 rounded-xl font-semibold transition-all ${
                plan.highlighted
                  ? 'bg-gradient-to-r from-[#F7B731] to-[#F26419] text-black hover:shadow-[0_0_30px_rgba(242,100,25,0.4)]'
                  : 'bg-white/5 border border-white/20 text-white hover:bg-white/10'
              }`}
            >
              {plan.price === 'Custom' ? 'Contact Sales' : 'Select Plan'}
            </button>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default PricingPlans;
